import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { midiNoteName, midiSolfege } from '@muzly/voice-engine';

import { freshHead } from './traceGeometry';
import { TrackCursorLine } from './TrackCursorLine';
import {
  SUSTAIN_TARGET_MIDI,
  deviationRatio,
  latchedTarget,
} from '../sustainGuide';

import type { PitchGraphPoint } from '../../../components';

/**
 * PitchBarTrack — **지속음 진단**(`RecordViz: 'sustain'`)의 시각화.
 *
 * **가로**: 손잡이는 처음부터 시안의 자리(`CURSOR_AT` = 40%)에 **고정**이고, 캡슐
 * 트랙("Ah" 그래프) 자체가 **오른쪽에서 왼쪽으로** 흐른다. 시작 시 캡슐의 왼쪽 끝은
 * 손잡이 글로우 **오른쪽에 떨어져** 있고(`STRIP_START_GAP`, 시안 `758:21810`), 시간이
 * 지나며 캡슐이 손잡이에 닿고 지나쳐 흘러간다 — 지나간 부분이 발성 기록(빨강/흰색)이다.
 *
 * **세로**: 손잡이가 기준음 대비 벗어남만큼 위아래로 움직인다 — 시안 주석이 손잡이를
 * `현재 피치 (위/아래, 회색/붉은)`라 부른다. 예전에 정지 프레임(`882:7830`)만 보고
 * 세로를 뺐던 결정을 이 주석이 뒤집는다. 점선은 손잡이의 세로 가이드다.
 *
 * **축의 눈금은 분석 창이다.** 트랙 전체 폭이 분석·보관 창(20초)과 같은 시간을 덮는다 —
 * 녹음 상한(30초)으로 눈금을 잡으면 흘러간 구간이 실제보다 촘촘해 보인다. 스크롤이
 * 시작된 뒤 왼쪽 끝을 지나 사라진 구간도 마지막 20초 안이면 리포트·파일에는 남는다
 * (자르는 쪽은 `withinAnalysisWindow`).
 *
 * 그리는 것 넷:
 * - **캡슐 트랙** — 왼쪽 끝이 테이크 시작 시각이고 시간이 지나며 왼쪽으로 흐른다.
 *   `Ah` 라벨은 캡슐이 아니라 손잡이 오른쪽 남은 영역 가운데에 붙는다 — 캡슐에 붙이면
 *   라벨이 같이 흘러가 몇 초 만에 화면을 떠난다.
 * - **채움** — **발성한 프레임 구간**. 어떤 음을 내든 소리를 내는 동안 채워진다 —
 *   MPT 진단이라 재는 것은 길이지 음정이 아니다. 무성 구간만 흰 조각으로 남는다
 *   (시안 `882:8088` "발성 끊김"이 그 상태다). 그 흰 구간이 5초 이어지면 상태기계가
 *   테이크를 끝낸다(`sustainIdleSec`).
 * - **손잡이 + 점선** — 타임라인의 현재 위치(가로) × 지금 내는 음의 벗어남(세로).
 *   점선은 손잡이의 세로 가이드다.
 * - **음이름 라벨** — 트랙 위 화면 가운데. 시안 `758:21810`이 이 자리를 `현재 음정`이라
 *   부르므로 **지금 내는 음**을 적는다(기준음이 아니다 — 기준음은 고정값이라 무엇을
 *   불러도 안 변한다). 안 잡히는 동안에는 목표인 기준음을 흐리게 남겨 어디를 향하는지
 *   보여준다. 가로로는 손잡이를 안 따라간다(시안이 화면 가운데 고정).
 *
 * **프롭 구동** — 네이티브 이벤트를 스스로 구독하지 않는다. 구독처는 `useVoiceCapture`
 * 하나뿐이라 어떤 전송(네이티브·브라우저 마이크·mock)이든 같은 화면을 본다.
 */

/** 캡슐 높이(px). 시안 `882:8044`의 채움 높이. */
const TRACK_H = 30;

/** 손잡이 지름(px). 시안 `882:7931`. */
const KNOB = 22;

/**
 * 손잡이의 가로 위치(트랙 폭 대비) — 시안(`758:5509`)의 손잡이 자리에 **고정**.
 * 시간이 흐르면 손잡이가 아니라 타임라인이 오른쪽에서 왼쪽으로 흐른다.
 */
export const CURSOR_AT = 0.4;

/**
 * 손잡이 세로 이동의 눈금 — `deviationRatio`의 ±1(= ±`DEVIATION_SPAN_CENTS`)을 점선
 * 가이드의 페이드 안쪽(중앙 ±28%)에 맞춘다.
 */
const DEVIATION_MAX_PCT = 28;

/**
 * 캡슐의 길이(트랙 폭 대비). 캡슐이 흐르는 동안 오른쪽 끝이 화면 안으로 들어오지 않을
 * 만큼 길어야 한다: 왼쪽 끝이 `CURSOR_AT + STRIP_START_GAP − 경과/spanSec`까지 밀리므로,
 * 지속음 상한 30초(경과/span = 1.5)에서도 `0.4 + 0.09 − 1.5 + 2.5 = 1.49 > 1` — 여유가
 * 남는다. 이 부등식은 `SUSTAIN_LIMIT_SEC`·`PCM_KEEP_SEC`에 묶여 있다 — spec이 못 박는다.
 */
export const STRIP_LEN = 2.5;

/**
 * 시작 시 캡슐 왼쪽 끝이 손잡이 중심에서 떨어져 있는 거리(트랙 폭 대비). 시안
 * `758:21810`: 캡슐 x=171, 손잡이 중심 x=137, 프레임 폭 375 → 34/375 ≈ 0.09. 손잡이에
 * 붙여 시작하면 캡슐이 처음부터 손잡이 글로우를 물고 있어 시안과 다르다 — 캡슐은
 * `gap × spanSec`(1.8초) 뒤에 손잡이에 닿고, 채움도 그 시점부터 태어난다.
 */
export const STRIP_START_GAP = 0.09;

/** `Ah` 라벨의 가로 위치 — 손잡이와 오른쪽 끝 사이 빈 영역의 가운데. */
const AH_LABEL_AT = (CURSOR_AT + 1) / 2;

interface PitchBarTrackProps {
  /** 캡처가 누적한 롤링 창. 타임라인·손잡이·채움이 모두 이 배열 하나에서 나온다. */
  points: PitchGraphPoint[];
  /** 이 테이크의 시간 원점(ms). 0이면 아직 첫 프레임이 안 왔다. */
  startMs: number;
  /**
   * 타임라인 가로축이 덮는 길이(초) — **분석 창과 같은 값**이어야 한다(`PCM_KEEP_SEC`).
   * 녹음 상한이 아니다: 상한까지 그리면 버려질 구간이 화면에 남는다.
   */
  spanSec: number;
  /**
   * 기준음(MIDI). 기본값은 들려주는 기준음 A4 — 사용자가 낸 옥타브로 내려 잡는 것은
   * `latchedTarget`이다.
   */
  targetMidi?: number;
}

interface Fill {
  /** 트랙 폭 대비 시작·끝(0..1). */
  from: number;
  to: number;
}

/**
 * 채워진 구간들. 연속한 **유성** 프레임을 하나의 막대로 묶는다 — 프레임마다 따로 그리면
 * 47fps짜리 점 1400개가 되고, 브라우저가 그걸 사각형 1400개로 그린다. 어떤 음이냐는
 * 안 본다: MPT 진단의 채움은 발성 여부다.
 */
export const fillSpans = (
  points: PitchGraphPoint[],
  startMs: number,
  spanSec: number,
): Fill[] => {
  if (spanSec <= 0) {
    return [];
  }
  const spanMs = spanSec * 1000;
  const spans: Fill[] = [];
  let open: Fill | null = null;
  for (const point of points) {
    const at = Math.max(0, Math.min(1, (point.t - startMs) / spanMs));
    if (point.midi != null) {
      open = open === null ? { from: at, to: at } : { from: open.from, to: at };
      continue;
    }
    if (open) {
      spans.push(open);
      open = null;
    }
  }
  return open ? [...spans, open] : spans;
};

/**
 * 음 하나를 시안 표기(`A3(라)`)의 조각으로. 가장 가까운 반음으로 반올림한다 — 실제
 * f0는 `219.98`처럼 떨어지므로 반올림 없이는 라벨이 반음마다 두 값 사이를 튄다.
 */
const noteLabel = (midi: number): { note: string; solfege: string } => ({
  note: midiNoteName(Math.round(midi)),
  solfege: midiSolfege(midi),
});

export const PitchBarTrack = ({
  points,
  startMs,
  spanSec,
  targetMidi = SUSTAIN_TARGET_MIDI,
}: PitchBarTrackProps) => {
  const { t } = useTranslation();
  // 손잡이가 "아직 잡히는 중"이라 거짓말하지 않게 — 오래됐거나 저신뢰면 null이다.
  const voiced = freshHead(points);
  const latest = points.length ? points[points.length - 1].t : startMs;
  // 캡슐 왼쪽 끝은 손잡이에서 `STRIP_START_GAP`만큼 오른쪽에서 출발해 경과만큼
  // 왼쪽으로 밀린다 — "지금"은 항상 손잡이 자리다.
  const elapsedRatio = spanSec > 0 ? (latest - startMs) / (spanSec * 1000) : 0;
  const stripLeft = CURSOR_AT + STRIP_START_GAP - elapsedRatio;
  // 채움은 캡슐 안에서 그리므로 좌표도 캡슐 기준이다: 축 = 캡슐 길이, 원점 = 캡슐
  // 왼쪽 끝이 손잡이에 닿는 시각(시작 + gap) — 출발 오프셋만큼 같이 밀어야 채움의
  // 머리가 손잡이 자리에서 태어난다(stripLeft + (경과 − gap) = CURSOR_AT).
  const spans = fillSpans(
    points,
    startMs + STRIP_START_GAP * spanSec * 1000,
    spanSec * STRIP_LEN,
  );
  // 축의 기준음은 **테이크마다 한 번만** 정한다(`latchedTarget`). 프레임마다 다시
  // 잡으면 축이 사용자를 따라다녀 아무 벗어남도 안 보이고, 옥타브를 접으면 축이
  // pitch-class 축이 되어 반옥타브 경계에서 손잡이가 꼭대기→바닥으로 떨어진다
  // (`.claude/20260916/DEBUG-14-18-00.md`). 한 번 잡은 값은 ref에 남긴다 — `points`는
  // 롤링 창이라 첫 발성 프레임이 테이크 도중 창 밖으로 흘러 나간다.
  const axis = useRef<{ startMs: number; target: number } | null>(null);
  if (axis.current?.startMs !== startMs) {
    axis.current = null;
  }
  if (axis.current === null) {
    const latched = latchedTarget(points, targetMidi);
    if (latched != null) {
      axis.current = { startMs, target: latched };
    }
  }
  const axisTarget = axis.current?.target;
  // 세로 = 기준음 대비 벗어남. 둘 중 하나라도 없으면 가운데다.
  // - 소리가 안 잡히면: 마지막 위치에 남겨 두면 회색 손잡이가 아직 그 음을 내는 중이라
  //   거짓말한다.
  // - 축이 아직 안 잡혔으면: A4를 임시 축으로 쓰면 A2를 내는 사람의 손잡이가 첫
  //   몇 프레임 동안 바닥에 박혔다가 축이 잡히는 순간 가운데로 튄다. 축이 없는 동안
  //   "가운데"는 아직 못 잰다는 뜻이지 틀린 값이 아니다.
  const deviation =
    voiced && axisTarget != null ? deviationRatio(voiced.midi, axisTarget) : 0;
  const knobTopPct = 50 - deviation * DEVIATION_MAX_PCT;

  return (
    <div
      role="img"
      aria-label={t('web.diagnosis.track.label')}
      // **백분율 높이(`h-full`)를 쓰지 않는다.** 온보딩 셸의 루트가 `min-h-dvh`라
      // 조상 체인의 높이가 indefinite이고, 그 위에서 `height:100%`는 0으로 해석된다.
      // 호출자가 flex 컨테이너로 감싸고 여기서는 늘어나기만 한다.
      className="relative w-full overflow-hidden"
    >
      {/* 손잡이의 세로 가이드. 시안에서는 이것이 유일한 세로 요소이고, 손잡이와 같은
          자리에 고정이다(시안 `882:7938`). 페이드가 끝나는 지점 = 손잡이 세로 이동의
          끝(`DEVIATION_MAX_PCT`) — 손잡이가 닿을 수 있는 구간까지는 온전히 보여야 한다. */}
      <TrackCursorLine
        at={CURSOR_AT}
        testId="pitch-track-line"
        solidHalfPct={DEVIATION_MAX_PCT}
      />

      {/* 현재 음정 — 시안은 이 라벨을 손잡이가 아니라 **화면 가운데**에 고정한다. */}
      <span
        data-testid="pitch-track-note"
        className="absolute left-1/2 -translate-x-1/2 text-base font-semibold tracking-[-0.5px] text-[hsl(var(--muted))]"
        style={{ top: `calc(50% - ${TRACK_H / 2 + 46}px)` }}
      >
        {t(
          'web.diagnosis.track.noteWithSolfege',
          noteLabel(voiced?.midi ?? targetMidi),
        )}
      </span>

      {/* 캡슐 트랙 — 왼쪽 끝이 테이크 시작이고 통째로 왼쪽으로 흐른다. 루트의
          `overflow-hidden`이 화면 밖으로 나간 부분을, 캡슐의 `overflow-hidden`이
          채움을 캡슐 모양으로 잘라 준다. */}
      <div
        data-testid="pitch-strip"
        aria-hidden="true"
        // `left`에 트랜지션을 걸지 않는다 — 매 프레임(~30fps) 갱신되는 값이라 100ms
        // 보간이 끝나기 전에 계속 재시작되어 CPU만 쓰고 부드러워지지도 않는다.
        className="absolute -translate-y-1/2 overflow-hidden rounded-full bg-[hsl(var(--surface-lowest))] shadow-[0_0_9px_rgba(251,35,59,0.28)]"
        style={{
          top: '50%',
          height: TRACK_H,
          left: `${stripLeft * 100}%`,
          width: `${STRIP_LEN * 100}%`,
        }}
      >
        {/* key는 `from`만 — 자라는 중인 구간은 `to`가 매 프레임 변해서, key에 넣으면
            React가 그 막대를 프레임마다 새로 만든다. 트랜지션도 안 건다: 매 프레임
            갱신되는 width에 100ms 보간은 재시작만 반복한다. */}
        {spans.map((span) => (
          <div
            key={span.from}
            data-testid="pitch-fill"
            className="absolute inset-y-0 bg-primary"
            style={{
              left: `${span.from * 100}%`,
              width: `${Math.max(0, span.to - span.from) * 100}%`,
            }}
          />
        ))}
      </div>

      {/* `Ah` 라벨 — 캡슐이 아니라 **손잡이 오른쪽 남은 영역의 가운데**에 고정한다.
          캡슐에 붙이면 라벨이 같이 흘러가 몇 초 만에 화면을 떠난다. */}
      <span
        className="absolute -translate-x-1/2 -translate-y-1/2 text-sm font-semibold tracking-[-0.5px] text-[hsl(var(--foreground))]"
        style={{ left: `${AH_LABEL_AT * 100}%`, top: '50%' }}
      >
        {t('web.diagnosis.track.vowel')}
      </span>

      {/* 손잡이 — 가로는 고정(채움이 여기서 태어난다), 세로는 지금 내는 음의 벗어남.
          색은 시안대로 둘뿐이다(`현재 피치 (위/아래, 회색/붉은)`): 발성이면 브랜드
          색 + 글로우, 음이 안 잡히면 회색(사양: "음이 detect 안되면 그레이로"). */}
      <div
        data-testid="pitch-marker"
        className="absolute -translate-x-1/2 -translate-y-1/2 motion-safe:transition-[top] motion-safe:duration-100"
        style={{ left: `${CURSOR_AT * 100}%`, top: `${knobTopPct}%` }}
      >
        <span
          className={
            voiced == null
              ? 'block rounded-full bg-muted-foreground/40 ring-4 ring-white'
              : 'block rounded-full bg-primary ring-4 ring-white shadow-[0_0_0_7px_hsl(var(--primary)/0.18)]'
          }
          style={{ width: KNOB, height: KNOB }}
        />
      </div>
    </div>
  );
};
